import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatDetail } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { formatINR } from "@/context/ConfigContext";
import PhotoGallery from "@/components/PhotoGallery";
import ModelViewer from "@/components/ModelViewer";
import MapPicker from "@/components/MapPicker";
import PapersViewer from "@/components/PapersViewer";
import EnquiryForm from "@/components/EnquiryForm";
import FavoriteButton from "@/components/FavoriteButton";
import ShareButtons from "@/components/ShareButtons";
import VastuPanel from "@/components/VastuPanel";
import PartnerContactCard from "@/components/PartnerContactCard";
import ReviewsSection from "@/components/ReviewsSection";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Boxes, MapPin, Ruler, Bed, Bath, Phone, User as UserIcon, Eye, FileText, ArrowLeft, Calendar, Pencil, BadgeCheck, Star } from "lucide-react";

function formatPrice(v) {
  if (v == null) return "-";
  const n = Number(v);
  if (n >= 1_00_00_000) return `₹ ${(n / 1_00_00_000).toFixed(2)} Cr`;
  if (n >= 1_00_000) return `₹ ${(n / 1_00_000).toFixed(2)} Lakh`;
  return `₹ ${n.toLocaleString("en-IN")}`;
}

export default function PropertyDetail() {
  const { id } = useParams();
  const { t, setLangByState, lang } = useLang();
  const { user } = useAuth();
  const [property, setProperty] = useState(null);
  const [tab, setTab] = useState("gallery");
  const [papersOpen, setPapersOpen] = useState(false);

  useEffect(() => {
    api.get(`/properties/${id}`)
      .then(({ data }) => {
        setProperty(data);
        if (data.state) setLangByState(data.state);
        if (data.model_path) setTab("3d");
      })
      .catch(() => setProperty(false));
    // eslint-disable-next-line
  }, [id]);

  if (property === false) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center">
        <h1 className="text-2xl font-bold mb-2">Property not found</h1>
        <Link to="/browse" className="text-primary underline">Back to listings</Link>
      </div>
    );
  }
  if (!property) return <div className="max-w-3xl mx-auto px-4 py-20 text-center text-muted-foreground">{t.common.loading}</div>;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-4">
        <Link to="/browse" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary">
          <ArrowLeft size={14} /> {t.common.back}
        </Link>
        <div className="flex items-center gap-2">
          <ShareButtons property={property} />
          <FavoriteButton propertyId={property.id} className="w-9 h-9" onLightBg />
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-6">
        {/* Left main content */}
        <div className="lg:col-span-8 space-y-6">
          <div>
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className="px-2 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-primary text-primary-foreground">
                {t.categories[property.category] || property.category}
              </span>
              {property.featured && (
                <span className="px-2 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-gradient-to-r from-amber-400 to-yellow-500 text-slate-900 flex items-center gap-1" data-testid="detail-featured-badge">
                  <Star size={10} fill="currentColor" /> Featured
                </span>
              )}
              {property.model_path && (
                <span className="px-2 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-secondary text-secondary-foreground flex items-center gap-1">
                  <Boxes size={10} /> 3D
                </span>
              )}
              {property.status && property.status !== "active" && (
                <span className={`px-2 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider ${
                  property.status === "sold" ? "bg-slate-900 text-white" :
                  property.status === "under_negotiation" ? "bg-amber-500 text-white" :
                  property.status === "rented" ? "bg-blue-600 text-white" :
                  "bg-slate-500 text-white"
                }`} data-testid="detail-status-badge">
                  {property.status.replace("_", " ")}
                </span>
              )}
              <span className="ml-auto text-xs text-muted-foreground flex items-center gap-1">
                <Eye size={12} /> {property.views || 0} {t.property.views}
              </span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-primary">{property.title}</h1>
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground mt-1">
              <MapPin size={14} className="text-secondary" />
              <span>{property.address}, {property.city}, {property.state}</span>
            </div>
          </div>

          <Tabs value={tab} onValueChange={setTab} className="w-full">
            <TabsList data-testid="property-tabs">
              <TabsTrigger value="gallery" data-testid="tab-gallery">{t.property.gallery}</TabsTrigger>
              {property.model_path && <TabsTrigger value="3d" data-testid="tab-3d">{t.property.threeD}</TabsTrigger>}
              <TabsTrigger value="map" data-testid="tab-map">{t.property.location}</TabsTrigger>
            </TabsList>
            <TabsContent value="gallery" className="mt-4">
              <PhotoGallery paths={property.photo_paths || []} />
            </TabsContent>
            {property.model_path && (
              <TabsContent value="3d" className="mt-4">
                <div className="h-[500px]">
                  <ModelViewer path={property.model_path} />
                </div>
                <p className="text-xs text-muted-foreground mt-2">Drag to rotate • Scroll to zoom</p>
              </TabsContent>
            )}
            <TabsContent value="map" className="mt-4">
              <MapPicker lat={property.latitude} lng={property.longitude} readOnly height={420} onPick={() => {}} />
            </TabsContent>
          </Tabs>

          <Card className="p-5">
            <div className="overline text-secondary mb-2">{t.property.description}</div>
            <p className="text-sm leading-relaxed whitespace-pre-wrap">{property.description}</p>
          </Card>

          <Card className="p-5 grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Stat icon={<Ruler size={16} />} label={t.property.size} value={`${property.size_value} ${t.units[property.size_unit]}`} />
            {property.bedrooms != null && <Stat icon={<Bed size={16} />} label={t.property.bed} value={property.bedrooms} />}
            {property.bathrooms != null && <Stat icon={<Bath size={16} />} label={t.property.bath} value={property.bathrooms} />}
            <Stat icon={<Calendar size={16} />} label={t.property.addedOn} value={new Date(property.created_at).toLocaleDateString()} />
          </Card>

          {property.facing_direction && (
            <VastuPanel category={property.category} direction={property.facing_direction} />
          )}
        </div>

        {/* Right sticky sidebar */}
        <div className="lg:col-span-4 space-y-4">
          {(user?.user_id === property.owner_id || user?.role === "admin") && (
            <Card className="p-4 space-y-3 bg-accent/40" data-testid="owner-controls">
              <div className="overline text-primary text-[10px]">Owner Controls</div>
              <div className="grid grid-cols-2 gap-2">
                <Link to={`/edit-property/${property.id}`}>
                  <Button variant="outline" size="sm" className="w-full" data-testid="edit-property-btn">
                    <Pencil size={12} className="mr-1" />Edit
                  </Button>
                </Link>
                <Select
                  value={property.status || "active"}
                  onValueChange={async (v) => {
                    try {
                      await api.patch(`/properties/${property.id}/status`, { status: v });
                      setProperty({ ...property, status: v });
                      toast.success(`Status: ${v.replace("_", " ")}`);
                    } catch (e) {
                      toast.error(formatDetail(e.response?.data?.detail));
                    }
                  }}
                >
                  <SelectTrigger className="h-9 text-xs" data-testid="status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="under_negotiation">Under Negotiation</SelectItem>
                    <SelectItem value="sold">Sold</SelectItem>
                    <SelectItem value="rented">Rented</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </Card>
          )}
          <Card className="p-5 sticky top-20">
            <div className="overline text-secondary mb-2">{t.property.price}</div>
            <div className="text-3xl font-bold text-primary tracking-tight" data-testid="detail-price">
              {formatPrice(property.price_value)}
            </div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground mt-1">
              {t.units[property.price_unit]}
            </div>

            <div className="mt-5 pt-5 border-t border-border space-y-2 text-sm">
              <div className="flex items-center gap-2 flex-wrap">
                <UserIcon size={14} className="text-muted-foreground" />
                <span className="font-medium">{property.owner_name}</span>
                {property.owner_kyc_status === "verified" && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-semibold" data-testid="detail-kyc-badge">
                    <BadgeCheck size={10} /> KYC Verified
                  </span>
                )}
              </div>
              {property.owner_phone ? (
                <a href={`tel:${property.owner_phone}`} className="flex items-center gap-2 text-primary hover:underline" data-testid="seller-phone">
                  <Phone size={14} />{property.owner_phone}
                </a>
              ) : (
                <div className="text-xs text-muted-foreground italic" data-testid="seller-phone-hidden">
                  Direct contact hidden. Call BhoomiMitra management below.
                </div>
              )}
            </div>

            {(property.paper_paths?.length || 0) > 0 && (
              <Button
                variant="outline"
                className="w-full mt-4 border-primary/40 text-primary hover:bg-primary hover:text-primary-foreground"
                onClick={() => setPapersOpen(true)}
                disabled={!user}
                data-testid="view-papers-btn"
              >
                <FileText size={14} className="mr-1.5" />
                {t.property.papers} ({property.paper_paths.length})
              </Button>
            )}
            {!user && (property.paper_paths?.length || 0) > 0 && (
              <p className="text-[11px] text-muted-foreground mt-2 text-center">
                {t.papers.loginRequired} <Link to="/login" className="text-primary underline">{t.nav.login}</Link>
              </p>
            )}

            {/* Redirected-contact card — hidden when owner is a Broker/Builder partner (their direct contact is shown via PartnerContactCard) */}
            {!["broker", "builder"].includes(property.owner_partner_role) && (
              <div className="mt-4 p-4 rounded-lg bg-primary/5 border border-primary/20" data-testid="bhoomimitra-contact-block">
                <div className="overline text-primary text-[9px]">Contact via BhoomiMitra</div>
                <div className="text-xs text-muted-foreground mt-1 leading-relaxed">
                  Buyer aur seller ki privacy ke liye direct number nahi diya jata. Kripya BhoomiMitra ko call karein — hum aapko seller se connect karayenge.
                </div>
                <div className="mt-3 space-y-2">
                  <a href="tel:+917014965172" className="block">
                    <Button className="w-full bg-secondary hover:bg-secondary/90 rounded-full" data-testid="contact-bhoomimitra-btn">
                      <Phone size={14} className="mr-1.5" />Call +91 70149 65172
                    </Button>
                  </a>
                  <a href="https://wa.me/917014965172" target="_blank" rel="noreferrer" className="block">
                    <Button variant="outline" className="w-full rounded-full border-primary/40 text-primary hover:bg-primary hover:text-primary-foreground" data-testid="whatsapp-bhoomimitra-btn">
                      WhatsApp BhoomiMitra
                    </Button>
                  </a>
                </div>
                <p className="text-[10px] text-muted-foreground mt-2">
                  Property ID: <span className="font-mono">{property.id}</span> — reference batayein call ke waqt
                </p>
              </div>
            )}
          </Card>

          {/* Partner (Broker/Builder) contact card — appears BEFORE enquiry form when applicable */}
          <PartnerContactCard property={property} />

          <EnquiryForm propertyId={property.id} propertyTitle={property.title} />

          {/* Reviews for Broker/Builder */}
          <ReviewsSection
            targetUserId={property.owner_id}
            partnerRole={property.owner_partner_role}
            businessName={property.owner_business_name}
            ownerName={property.owner_name}
          />
        </div>
      </div>

      <PapersViewer open={papersOpen} onOpenChange={setPapersOpen} paths={property.paper_paths || []} />
    </div>
  );
}

function Stat({ icon, label, value }) {
  return (
    <div>
      <div className="text-muted-foreground flex items-center gap-1.5 text-xs mb-1">{icon}{label}</div>
      <div className="font-semibold">{value}</div>
    </div>
  );
}
